import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import type { SortingState } from '@tanstack/react-table';
import type { PaginationState } from '@/components';
import {
  DEFAULT_PAGINATION,
  DEFAULT_SORTING,
} from '../constants/efax-search-defaults';
import type { EfaxSearchState } from '../types';
import { mapEfaxSearchInput } from '../utils/map-efax-query-params';
import { useSearchAnonymousRecordFaxes } from './search-anonymous-record-faxes';

interface UseEfaxQueueOptions {
  appliedSearch: EfaxSearchState;
  hasSearched: boolean;
}

export const useEfaxQueue = ({
  appliedSearch,
  hasSearched,
}: UseEfaxQueueOptions) => {
  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const searchInput = useMemo(
    () => mapEfaxSearchInput(appliedSearch, sorting, pagination),
    [appliedSearch, sorting, pagination],
  );

  const { data, isLoading, isError } = useSearchAnonymousRecordFaxes(
    searchInput,
    { enabled: hasSearched },
  );

  const resetPagination = useCallback(() => {
    setPagination((current) => ({ ...current, page: 0 }));
  }, []);

  const resetPaginationAndSorting = useCallback(() => {
    setPagination(DEFAULT_PAGINATION);
    setSorting(DEFAULT_SORTING);
  }, []);

  const onSortingChange: Dispatch<SetStateAction<SortingState>> = useCallback(
    (updater) => {
      setSorting(updater);
      resetPagination();
    },
    [resetPagination],
  );

  return {
    items: data?.records ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isError,
    sorting,
    onSortingChange,
    pagination,
    setPagination,
    resetPagination,
    resetPaginationAndSorting,
  };
};
